/**
 * Hook para buscar dados de unidades pendentes em Alvará de Funcionamento e AVCB
 * 
 * Critério de Regularização:
 * - Considera apenas documentos de "Alvará de Funcionamento" e "AVCB"
 * - Unidade REGULAR = TODOS os documentos (Alvará + AVCB) devem estar Regular
 * - Unidade IRREGULAR = Qualquer documento não-Regular
 */
import { useQuery } from '@tanstack/react-query';
import { API_BASE_URL } from '@/lib/api';

const API_BASE = `${API_BASE_URL}/claro/api`;

interface FilterParams {
  regional?: string;
  uf?: string;
}

// Documento irregular com detalhes
export interface IrregularDoc {
  tipo: 'Alvará' | 'AVCB';
  tipoCompleto: string;
  docName: string | null; // Nome/identificador do documento (ex: ORD_01.685.903/0053-47)
  status: string;
  categoria: 'Em Andamento' | 'Irregular';
  motivo: string | null;
  fornecedor: string;
  vencimento: string | null;
  activeProcessGuid: string | null;
}

// Unidade pendente com nova estrutura
export interface PendenciaUnit {
  UnitId: number;
  UnitName: string;
  UnitGuid: string;
  cnpj: string;
  endereco: string;
  RegionalName: string | null;
  CityStateName: string | null;
  UF: string | null;
  status: 'regular' | 'em_andamento' | 'irregular' | 'sem_doc';
  // Resumo do motivo para exibição rápida
  motivoResumo: string;
  // Lista detalhada de documentos irregulares
  documentosIrregulares: IrregularDoc[];
  // Tem Alvará? Tem AVCB?
  temAlvara: boolean;
  temAvcb: boolean;
  // Principal fornecedor responsável
  fornecedor: string;
  // ProcessGuids dos documentos (para buscar comentários) - arrays pois pode ter múltiplos documentos
  alvaraProcessGuids: string[];
  avcbProcessGuids: string[];
  // Mapeamento processGuid -> nome do documento (ex: "Alvará de Funcionamento - ORD_01.685.903/0053-47")
  processGuidToDocName: Record<string, string>;
}

export interface DocTypeKpis {
  regulares: number;
  emAndamento: number;
  irregular: number;
  semDocumento: number;
  pendentes: number;
}

export interface PendenciasKpis {
  totalUnidades: number;
  regulares: number;
  pendentes: number;
  emAndamento: number;
  irregular: number;
  semDocumento: number;
  // KPIs por tipo de documento
  alvara: DocTypeKpis;
  avcb: DocTypeKpis;
}

export interface PendenciasResponse {
  kpis: PendenciasKpis;
  units: PendenciaUnit[];
}

async function fetchJson<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(`${API_BASE}${endpoint}`, window.location.origin);
  
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
    });
  }
  
  const response = await fetch(url.toString());
  
  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }
  
  const json = await response.json();
  return json.result ?? json;
}

/**
 * Hook para buscar unidades pendentes em Alvará de Funcionamento
 * Usa o endpoint do servidor que já calcula fornecedores corretamente
 */
export function usePendencias(filters?: FilterParams) {
  return useQuery({
    queryKey: ['pendencias', 'alvara', filters],
    queryFn: () => fetchJson<PendenciasResponse>('/units/pendencias-alvara', filters as Record<string, string>),
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 30, // 30 minutos
  });
}

/**
 * Hook para buscar lista de regionais
 */
export function useRegionals() {
  return useQuery({
    queryKey: ['units', 'regionals'],
    queryFn: async () => {
      const data = await fetchJson<string[]>('/units/regionals');
      return Array.isArray(data) ? data : [];
    },
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  });
}

/**
 * Registro de mudança de status
 */
export interface StatusChangeRecord {
  Id: number;
  Guid: string;
  DateHistorical: string;
  UserHistoricalName: string;
  EntityLinkedName: string;
  DocumentGuid: string;
  DocumentTypeName: string;
  DocumentName: string;
  OldStatusDocument: number;
  OldStatusDocumentName: string;
  NewStatusDocument: number;
  NewStatusDocumentName: string;
}

interface StatusHistoryParams {
  dateStart?: string;
  dateEnd?: string;
  unitCode?: string;
  length?: number;
}

interface StatusHistoryResponse {
  data: StatusChangeRecord[];
  total: number;
}

/**
 * Hook para buscar histórico de mudanças de status de uma unidade
 * @param unitCode Código da unidade (ex: "ERO0023")
 * @param enabled Se a query deve ser executada
 */
export function useStatusHistory(unitCode: string | null, enabled = true) {
  return useQuery({
    queryKey: ['status-history', unitCode],
    queryFn: async () => {
      if (!unitCode) return { data: [], total: 0 };
      
      // Busca todo histórico da unidade (sem filtro de data)
      const params: StatusHistoryParams = {
        unitCode,
        length: 100,
      };
      
      return fetchJson<StatusHistoryResponse>('/documents/status-history', params as Record<string, string>);
    },
    enabled: enabled && !!unitCode,
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * Comentário de processo do PropertyDocs
 */
export interface ProcessComment {
  Guid: string;
  ProcessChecklistGuid: string | null;
  ProcessChecklistName: string | null;
  ProcessPendencyId: number | null;
  ProcessPendencyGuid: string | null;
  ProcessPendencyTypeName: string | null;
  ProcessFlowStepId: number | null;
  ProcessFlowStepGuid: string | null;
  ProcessFlowStepName: string | null;
  EncryptedFileRelationships: unknown[];
  TextComment: string;
  UserRegistrationName: string;
  DateRegistration: string;
  DateDeleted: string | null;
  Deleted: boolean;
  Private: boolean;
  UserDeleteName: string | null;
  OwnComment: boolean;
}

interface ProcessCommentsResponse {
  data: ProcessComment[];
}

/**
 * Hook para buscar comentários de um processo
 * @param processGuid GUID do processo
 * @param enabled Se a query deve ser executada
 */
export function useProcessComments(processGuid: string | null, enabled = true) {
  return useQuery({
    queryKey: ['process-comments', processGuid],
    queryFn: async () => {
      if (!processGuid) return { data: [] };
      
      const params = {
        processGuid,
        length: '1000',
      };
      
      return fetchJson<ProcessCommentsResponse>('/documents/process-comments', params);
    },
    enabled: enabled && !!processGuid,
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * Comentário com informação de qual processo veio
 */
export interface ProcessCommentWithSource extends ProcessComment {
  sourceProcessGuid: string;
}

/**
 * Hook para buscar comentários de múltiplos processos
 * Combina todos os comentários e ordena por data
 * @param processGuids Array de GUIDs dos processos
 * @param enabled Se a query deve ser executada
 */
export function useMultipleProcessComments(processGuids: string[], enabled = true) {
  return useQuery({
    queryKey: ['process-comments-multiple', processGuids.sort().join(',')],
    queryFn: async () => {
      if (!processGuids.length) return { data: [] as ProcessCommentWithSource[] };
      
      // Busca comentários de todos os processos em paralelo
      const promises = processGuids.map(async (processGuid) => {
        const params = {
          processGuid,
          length: '1000',
        };
        try {
          const result = await fetchJson<ProcessCommentsResponse>('/documents/process-comments', params);
          // Adiciona sourceProcessGuid a cada comentário
          return (result.data || []).map(comment => ({
            ...comment,
            sourceProcessGuid: processGuid,
          }));
        } catch {
          return [];
        }
      });
      
      const results = await Promise.all(promises);
      
      // Combina todos os comentários
      const allComments = results.flat();
      
      // Remove duplicados por Guid e ordena por data (mais recente primeiro)
      const uniqueComments = Array.from(
        new Map(allComments.map(c => [c.Guid, c])).values()
      ).sort((a, b) => 
        new Date(b.DateRegistration).getTime() - new Date(a.DateRegistration).getTime()
      );
      
      return { data: uniqueComments };
    },
    enabled: enabled && processGuids.length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 30,
  });
}
